import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import ReducerConstants from '../../../../../_shared/constants/reducers';
import renderLocale from '../../../../../_shared/helpers/renderLocale';
import convertToCurrency from '../../../../../_shared/utils/convertToCurrency';
import { BidSource } from '../../../../../_shared/types/auction';
import MixpanelConfig from '../../../../../_shared/constants/mixpanelConfig';
import ROUTES from '../../../../Routes/constants';
import { FormattedBidTime, FormattedAuctionTime } from './FormattedBidTime';
import { StyledBidHistoryPreview } from '../Styles';
import {
  StyledBidEntry,
  StyledBidUser,
  StyledAuctionStateUpdate
} from './Styles';
import useAnalytics from '../../../../../_shared/hooks/useAnalytics';

const Preview = ({ variant, bidContext }) => {
  const { auctionSummary, bidList, numberOfBids, id } = bidContext ?? {};
  const track = useAnalytics();

  const [user] = useSelector(
    state => [state[ReducerConstants.AUTH]?.user],
    shallowEqual
  );
  const userLocale = renderLocale(user?.auth);
  const [bids, setBids] = useState([]);
  const [users, setUsers] = useState({});
  const [dealers, setDealers] = useState({});

  /**
   * Checks if the user can see the bidder name/company
   * Don't display for own bids
   */
  const canSeeBid = bidUser =>
    !!bidUser?.companyId &&
    bidUser?.id !== user?.id &&
    bidUser?.fullName &&
    bidUser?.companyName;

  /**
   * TR4 Link
   */
  const getUserProfileLink = (userId, dealerId) =>
    `${process.env.REACT_APP_TR4_ENDPOINT}profile/user/${userId}?dealerId=${dealerId}`;

  const trackDealerProfileView = () => {
    const location = window.location.href.includes(ROUTES.BUYING)
      ? BidSource.MINI_VDP
      : BidSource.VDP_OVERLAY;

    track(MixpanelConfig.names.viewDealerProfilePage, {
      source:
        variant === 'modal'
          ? `${location} Bidbox history`
          : `${location} Full history`
    });
  };

  /**
   * Bid List
   */
  useEffect(() => {
    if (
      bidList?.bids &&
      bidList?.users &&
      bidList?.bids.length > 0 &&
      id === bidList?.bids?.[0]?.tradeId
    ) {
      setBids(bidList.bids);

      const usersById = {};
      const dealersById = {};
      bidList.users.map(bidUser => {
        usersById[bidUser.id] = bidUser;
        if (bidUser?.companyId) {
          dealersById[bidUser.companyId] = bidUser.companyName;
        }
        return bidUser;
      });
      setUsers(usersById);
      setDealers(dealersById);
    }
  }, [bidList, id, user]);

  return (
    <>
      {numberOfBids > 0 ? (
        bids.map(bid => (
          <StyledBidEntry key={bid.id}>
            <StyledBidEntry.Wrapper>
              <StyledBidEntry.Amount>
                {convertToCurrency(bid.amount)}
              </StyledBidEntry.Amount>
              <StyledBidEntry.Time>
                <FormattedBidTime bid={bid} user={user} />
              </StyledBidEntry.Time>
            </StyledBidEntry.Wrapper>
            {canSeeBid(users[bid.userId]) && (
              <StyledBidUser.Wrapper>
                <StyledBidUser.Name>
                  <a
                    href={getUserProfileLink(bid.userId, bid.dealerId)}
                    onClick={trackDealerProfileView}
                  >
                    {users[bid.userId]?.fullName}
                  </a>
                </StyledBidUser.Name>
                <StyledBidUser.CompanyName>
                  {dealers[bid.dealerId]}
                </StyledBidUser.CompanyName>
              </StyledBidUser.Wrapper>
            )}
          </StyledBidEntry>
        ))
      ) : (
        <StyledBidHistoryPreview.NoBidMessage>
          {/* Be the first to bid! */}
          <FormattedMessage id="vdp.bid.beFirst" />
        </StyledBidHistoryPreview.NoBidMessage>
      )}
      <StyledAuctionStateUpdate>
        <StyledAuctionStateUpdate.State>
          {/* Auction launched */}
          <FormattedMessage id="vdp.bid.auctionLaunched" />
        </StyledAuctionStateUpdate.State>
        <StyledAuctionStateUpdate.Time>
          <FormattedAuctionTime
            startTime={auctionSummary?.startTime}
            userLocale={userLocale}
          />
        </StyledAuctionStateUpdate.Time>
      </StyledAuctionStateUpdate>
    </>
  );
};

export default Preview;
